import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { DxValidationGroupComponent, DxValidationGroupModule } from 'devextreme-angular/ui/validation-group';
import { ArgentineCbuPaymentComponent, CardModel, stripDocumentMask } from '@pedix-workspace/angular-ui-payment';
import { ZenriseBankAccount, ZenriseDebtData, ZenriseNewSubscriptionReactivation, ZenrisePaymentMethodType } from '@pedix-workspace/utils';
import { ToastrService } from 'ngx-toastr';
import { RegistrationService } from '../../registration.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import confetti from 'canvas-confetti';
import { DeviceDetectorService } from 'ngx-device-detector';
import { addDays, format } from 'date-fns';
import { environment } from '../../../../../environments/environment';
import { AnalyticsService } from '../../../shared/analytics.service';
import { CurrencyFormatPipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { ArgentinePaymentComponent } from '../../step-subscription/argentine-payment/argentine-payment.component';
const planNames = {
  BASIC: 'Plan Principiante',
  INTERMEDIATE: 'Plan Especialista',
  ADVANCED: 'Plan PRO',
};

@Component({
    selector: 'app-reactivate-payment',
    templateUrl: './reactivate-payment.component.html',
    styleUrls: ['./reactivate-payment.component.scss'],
    imports: [
        DxValidationGroupModule,
        ArgentinePaymentComponent,
        ArgentineCbuPaymentComponent,
        ButtonComponent,
        AlertComponent,
        CurrencyFormatPipe,
        TranslocoDirective,
    ]
})
export class ReactivatePaymentComponent implements OnInit {
  @ViewChild('validationGroup')
  validationGroup: DxValidationGroupComponent;

  loading = true;
  sending = false;
  cardModel: CardModel = {
    cardNumber: '',
    cardName: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    dni: '',
    creditCard: null,
    documentType: undefined,
  };
  cbuModel: ZenriseBankAccount = {
    holderName: '',
    cbuNumber: '',
    holderIdNumber: '',
    bankId: null,
  };
  cbuContact: ZenriseNewSubscriptionReactivation['contact'];
  planName = '';
  paymentSuccess = false;
  startBillingDate: string;
  originalPaymentMethod: ZenrisePaymentMethodType = 'card';
  selectedPaymentMethod: ZenrisePaymentMethodType = 'card';
  currentStep: 'debt' | 'payment' = 'debt';

  @Input() subscriptionDebt: ZenriseDebtData;
  @Input() error: string = null;

  private toastrService = inject(ToastrService);
  private registrationService = inject(RegistrationService);
  readonly banks = this.registrationService.banks;
  private t = inject(TranslocoService);
  private deviceService = inject(DeviceDetectorService);
  private analyticsService = inject(AnalyticsService);

  get hasDebt(): boolean {
    return this.subscriptionDebt?.debt > 0;
  }

  get needsNewSubscription(): boolean {
    return this.selectedPaymentMethod !== this.originalPaymentMethod || this.originalPaymentMethod === 'cbu';
  }

  get isCbuSelected(): boolean {
    return this.selectedPaymentMethod === 'cbu';
  }

  get hasMultipleSteps(): boolean {
    return this.isCbuSelected && this.hasDebt;
  }

  get showCardForm(): boolean {
    if (!this.needsNewSubscription) return true;
    if (this.isCbuSelected) return this.currentStep === 'debt' && this.hasDebt;
    return true;
  }

  get showCbuForm(): boolean {
    if (!this.isCbuSelected) return false;
    if (!this.hasDebt) return true;
    return this.currentStep === 'payment';
  }

  ngOnInit(): void {
    this.planName = planNames[this.subscriptionDebt.subscription?.plan];
    this.originalPaymentMethod = this.subscriptionDebt.paymentMethodType || 'card';
    this.selectedPaymentMethod = this.originalPaymentMethod;
    if (this.isCbuSelected) {
      this.registrationService.loadBanks();
    }
    this.startBillingDate =
      this.subscriptionDebt.daysRemaining > 0
        ? format(addDays(new Date(), this.subscriptionDebt.daysRemaining), 'dd/MM/yy')
        : null;

    this.cbuContact = {
      email: this.subscriptionDebt.contact?.email || '',
      firstName: '',
      lastName: '',
      holderDocumentNumber: '',
      phoneNumber: '',
    };
  }

  onPaymentMethodChange(method: ZenrisePaymentMethodType) {
    this.selectedPaymentMethod = method;
    this.currentStep = 'debt';
    if (this.isCbuSelected) {
      this.registrationService.loadBanks();
    }
    this.validationGroup?.instance?.reset();
  }

  async processPayment() {
    if (!this.validationGroup.instance.validate().isValid) {
      this.toastrService.error(this.t.translate('general.error'));
      return;
    }

    if (this.hasMultipleSteps && this.currentStep === 'debt') {
      this.currentStep = 'payment';
      return;
    }

    this.sending = true;

    try {
      if (!this.needsNewSubscription) {
        await this.registrationService.createArgentineReactivation(
          this.cardModel,
          this.subscriptionDebt,
        );
      } else {
        await this.registrationService.createNewSubscriptionReactivation(
          this.cardModel,
          this.selectedPaymentMethod,
          this.subscriptionDebt,
          this.isCbuSelected ? this.cbuModel : undefined,
          {
            ...this.cbuContact,
            holderDocumentNumber: this.isCbuSelected ? stripDocumentMask(this.cbuModel.holderIdNumber) : stripDocumentMask(this.cardModel.dni),
          },
        );
      }

      this.paymentSuccess = true;
      this.analyticsService.sendReactivateSubscriptionEvent({
        slug: this.subscriptionDebt.slug,
        reactivateError: false,
      });

      if (!this.deviceService.isMobile()) {
        this.createConfetti();
      }
    } catch ({ errorTitle, errorMessage }) {
      this.toastrService.error(errorMessage, errorTitle);
      this.analyticsService.sendReactivateSubscriptionEvent({
        slug: this.subscriptionDebt.slug,
        reactivateError: true,
      });
    } finally {
      this.sending = false;
    }
  }

  goBackToDebtStep() {
    this.currentStep = 'debt';
    this.validationGroup?.instance?.reset();
  }

  goToPedix() {
    location.href = `${environment.siteUrl}/acceso?username=${encodeURIComponent(this.subscriptionDebt.slug)}`;
  }

  createConfetti() {
    const end = Date.now() + 6 * 1000;

    const colors = ['#3b3c66', '#f57d7e'];

    (function frame() {
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors,
        zIndex: 20000,
      });
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors,
        zIndex: 20000,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    })();
  }

}
